// TASK-20260825-twap-parent-rows-illegible-in-trades-tab — a working TWAP parent must not read as a hung trade.
//
// A TWAP parent's `AutoTradeOrders` row is a WRITE-AHEAD INTENT FOR THE WHOLE PARENT, not a trade, and it
// stays `Placing` with a null OrderId BY DESIGN until the parent settles (CoinbaseLiveTradeExecutor: a
// synthetic parent returns a Working fill immediately rather than burning the tick's deadline polling it).
// The owner watched four such rows sit at "Placing" for ten minutes while their parents were demonstrably
// progressing 6/9 → 7/9 cycles. The behavior was right; the screen could not say so.
//
// The join needs no server work. For a TWAP parent the ledger row's key IS the twp… parent id — minted
// before the write-ahead so reconcile-by-client-id, the restart rebuild and the divergence fold all find the
// parent by its prefix — and that equals TwapParentOrders.ParentOrderId. So a parent row is self-identifying
// by prefix (isParentId), and its progress comes from the parents page the tab already fetches.
//
// Extracted from the component because this project has NO component-mount harness (vitest + svelte, no
// testing-library): logic written inline in a .svelte file is only ever covered transitively. That is exactly
// how the no-resolved-org skip went missing in Orders.svelte (TASK-20260824), which is why ordersTwapParents.js
// exists — this is the same pattern for the same reason.
import { isParentId } from './twapReport.js';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Does this trades list contain a TWAP parent row at all? Callers gate the parents fetch on this so a
 * deployment that never ran TWAP pays for no request.
 *
 * Exported (rather than inlined at the call site) so there is exactly ONE prefix rule: `isParentId`, which
 * mirrors `TwapIds.IsParentId` server-side. An inline `startsWith('twp')` at the call site would accept the
 * bare string `'twp'` that `isParentId` rejects — a gate and a decoration predicate that disagree is the
 * shape of bug that only surfaces after someone edits one of them.
 */
export function hasTwapParentRows(trades) {
  return Array.isArray(trades) && trades.some((t) => isParentId(t?.clientOrderId));
}

/**
 * Index a `/api/twap/parents` page by parent order id.
 *
 * Tolerates a null/failed/garbage page by returning an empty Map rather than throwing — a parents fetch
 * that failed must degrade to today's rendering, never break the trades table (AC 9).
 *
 * @param {unknown} parents the page's `items`, or anything at all
 * @returns {Map<string, object>} parentOrderId → summary
 */
export function twapParentsById(parents) {
  const map = new Map();
  if (!Array.isArray(parents)) return map;
  for (const p of parents) {
    const id = p?.parentOrderId;
    if (typeof id === 'string' && id) map.set(id, p);
  }
  return map;
}

/** How a terminal parent names itself. Mirrors STATUS_LABELS in twapReport.js (one vocabulary, two surfaces). */
const TERMINAL_LABELS = {
  Done: 'Done',
  PartialFilled: 'Partially filled',
  Cancelled: 'Cancelled',
  Rejected: 'Rejected',
  Abandoned: 'Abandoned',
};

/**
 * The pill tone for a parent. A WORKING parent gets its own tone — it is progressing by design and must
 * read as active, not as the blue "pending" an ordinary stuck placement wears.
 *
 * Every TERMINAL state gets a non-empty tone. `Abandoned` matters most: the shared table's `statusClass`
 * has no arm for it, so it fell through to unstyled muted grey — visually identical to a benign `Cancelled`
 * — even though TwapParentStatus documents it as a parent recovery COULD NOT RESUME, always alerted. A
 * failed recovery must not look like a normal cancel.
 */
function toneFor(status, working) {
  if (working) return 'status-twap-working';
  switch (status) {
    case 'Done': return 'status-done';
    case 'PartialFilled': return 'status-partial';
    case 'Cancelled': return 'status-cancelled';
    case 'Rejected':
    case 'Abandoned': return 'status-rejected';
    default: return 'status-pending';
  }
}

/** `TWAP 7/9`, or `TWAP 7 cycles` when the plan is not yet known (CyclesPlanned is nullable). */
function cycleCount(summary) {
  const done = isNum(summary.cyclesCompleted) ? summary.cyclesCompleted : 0;
  if (isNum(summary.cyclesPlanned)) return `TWAP ${done}/${summary.cyclesPlanned}`;
  return `TWAP ${done} cycle${done === 1 ? '' : 's'}`;
}

/**
 * The visible badge. A working parent shows the bare progress — its tone already says "working". A SETTLED
 * parent appends WHICH WAY it settled, because a lone cycle count hides that: `TWAP 3/9` on an abandoned
 * parent reads as a calm progress note when six cycles of intended notional never executed. The status must
 * be in the text, not only in a hover title (invisible on touch, and not row content for a screen reader).
 */
function progressLabel(summary, working) {
  const count = cycleCount(summary);
  if (working) return count;
  const named = TERMINAL_LABELS[summary.status];
  return named ? `${count} · ${named}` : count;
}

/**
 * Decorate ledger-derived trade rows so a TWAP parent reads as a working parent with its cycle progress
 * instead of a bare "Placing" trade.
 *
 * Every row gets `isTwapParent` and `twap` (null unless a parent summary resolved). A recognized, resolved
 * parent additionally has:
 *   - `status` replaced by the parent's DURABLE status (the ledger row's `Placing` is stale by design),
 *   - `qty`/`price` replaced by the parent's CUMULATIVE fill, so the numbers match what actually executed,
 *   - `intendedQty`/`intendedPrice` preserving what the ledger row claimed.
 *
 * Rows are COPIED, never mutated — the store's data stays clean.
 *
 * A `twp…` row whose summary does not resolve is returned untouched (AC 9). That is not defensive padding:
 * parents created before the RequestId fix carry `RequestId = NULL` (no heuristic backfill was made), so the
 * `requestId` filter genuinely cannot find them, and those rows must keep rendering exactly as they do today.
 * Rows with no `clientOrderId` at all — every backtest fill and the portfolio blotter — pass straight through
 * (AC 11), which is what keeps the shared TradesTable honest for its other two consumers.
 *
 * @param {unknown} trades  the neutral `StrategyTradeRecord[]`
 * @param {Map<string, object>} [parentsById] from {@link twapParentsById}
 * @returns {object[]} decorated copies, in the input order
 */
export function decorateTrades(trades, parentsById) {
  if (!Array.isArray(trades)) return [];
  const byId = parentsById instanceof Map ? parentsById : new Map();

  return trades.map((t) => {
    const row = { ...t, isTwapParent: false, twap: null };
    if (!isParentId(t?.clientOrderId)) return row;

    row.isTwapParent = true;

    const summary = byId.get(t.clientOrderId);
    if (!summary) return row; // unresolvable → exactly today's rendering (AC 9)

    const working = summary.status === 'Working';
    row.twap = {
      working,
      status: summary.status,
      cyclesCompleted: isNum(summary.cyclesCompleted) ? summary.cyclesCompleted : null,
      cyclesPlanned: isNum(summary.cyclesPlanned) ? summary.cyclesPlanned : null,
      label: progressLabel(summary, working),
      tone: toneFor(summary.status, working),
      parentOrderId: summary.parentOrderId,
    };

    // The durable parent status is the truth; the ledger row's `Placing` only means "not settled yet".
    row.status = summary.status;

    // Keep what the intent claimed, then show what has actually executed.
    row.intendedQty = t.qty;
    row.intendedPrice = t.price;
    if (isNum(summary.filledQty)) row.qty = summary.filledQty;
    // Before the first fill there is no average price — keep the arrival/intended price rather than blank it.
    if (isNum(summary.avgFillPrice)) row.price = summary.avgFillPrice;

    return row;
  });
}
